import {useTheme} from '@mui/material'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import type {FC, RefObject} from 'react'
import {useCallback} from 'react'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useSnackbar} from '@hooks/useSnackbar'
import ActionsMenu, {
  type ActionMenuItem,
} from '@containers/Dashboards/AgentChat/components/messages/ActionsMenu'
import {
  copyChartPng,
  downloadChartPng,
  isChartClipboardCopySupported,
  slugifyChartName,
  type ChartLegendEntry,
  type ChartRenderOptions,
} from './chartExport'

interface Props {
  svgRef: RefObject<SVGSVGElement | null>
  title?: string
  /** Legend to composite above the plot; omit when the SVG already draws it (pie). */
  legend?: ChartLegendEntry[]
}

/**
 * Builds the chart's export actions (download PNG, copy PNG) and renders them in the
 * shared {@link ActionsMenu}. Colours + font come from the theme so exports match the UI.
 */
const ChartActionsMenu: FC<Props> = ({svgRef, title, legend}) => {
  const theme = useTheme()
  const [t] = useCustomTranslation('alpinist')
  const [showNotification] = useSnackbar()

  const buildRenderOptions = useCallback((): ChartRenderOptions | null => {
    const svg = svgRef.current
    if (!svg) return null
    return {
      svg,
      background: theme.palette.background.paper,
      titleColor: theme.palette.text.primary,
      legendColor: theme.palette.text.secondary,
      fontFamily: theme.typography.fontFamily ?? 'sans-serif',
      title: title?.trim() || undefined,
      legend: legend?.length ? legend : undefined,
    }
  }, [svgRef, title, legend, theme])

  const handleDownload = useCallback(() => {
    const opts = buildRenderOptions()
    if (!opts) return
    const trimmed = title?.trim()
    const base = trimmed
      ? slugifyChartName(trimmed)
      : `alpinist-chart-${new Date().toISOString().slice(0, 10)}`
    void downloadChartPng({...opts, filename: `${base || 'alpinist-chart'}.png`})
  }, [buildRenderOptions, title])

  const handleCopy = useCallback(() => {
    const opts = buildRenderOptions()
    if (!opts) return
    // Runs inside the menu-item click, so the clipboard write keeps the user gesture.
    copyChartPng(opts)
      .then(() => showNotification({message: t('chart_copied'), severity: 'success'}))
      .catch(() => showNotification({message: t('chart_copy_failed'), severity: 'error'}))
  }, [buildRenderOptions, showNotification, t])

  const items: ActionMenuItem[] = [
    {
      key: 'download-png',
      label: t('download_as_png'),
      icon: <DownloadOutlinedIcon fontSize="small" />,
      onClick: handleDownload,
    },
  ]
  if (isChartClipboardCopySupported()) {
    items.push({
      key: 'copy-png',
      label: t('copy_to_clipboard'),
      icon: <ContentCopyOutlinedIcon fontSize="small" />,
      onClick: handleCopy,
    })
  }

  return <ActionsMenu items={items} tooltip={t('chart_options')} />
}

export default ChartActionsMenu
